import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  IconButton,
  Alert,
  useTheme,
  Paper,
  Pagination,
} from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import DatePicker from '../shared/components/forms/HotelDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import { getNoShowBookings } from '../services/api';
import NoShowDrawer from '../components/booking/NoShowDrawer';
import BookingOpsBookingCard from '../components/booking/BookingOpsBookingCard';
import DashboardHeader from '../components/common/DashboardHeader';
import EmptyStateCard from '../components/common/EmptyStateCard';
import { detectThemeSync, watchThemeChanges } from '../utils/themeUtils';
import { getDashboardPageShellSx, getDashboardPaperSx } from '../utils/dashboardSurfaceSx';
import { isMobileShellLayout } from '../utils/mobileShellLayout';
import { getBookingOpsGridItemSx } from '../utils/bookingOperationsMobileLayout';
import { getHotelDateFormat } from '../utils/dateUtils';

const CUSTOM_BORDER_COLOR = '#4ce1b6';

const NoShowPage = () => {
  const normalizeBookingId = (value) => {
    const parsedId = Number(value);
    return Number.isInteger(parsedId) && parsedId > 0 ? parsedId : null;
  };
  const theme = useTheme();
  // Initialize theme synchronously to prevent flash
  const [currentTheme, setCurrentTheme] = useState(() => detectThemeSync());
  const isMobileLayout = isMobileShellLayout();
  const isDark = currentTheme === 'dark';

  // Watch for theme changes
  useEffect(() => {
    const cleanup = watchThemeChanges((newTheme) => {
      setCurrentTheme(newTheme);
    });
    return cleanup;
  }, []);

  const [selectedDate, setSelectedDate] = useState(moment());
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [actionNotice, setActionNotice] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12; // 3 rows × 4 cards per row
  const fetchAbortController = useRef(null);
  const debounceTimer = useRef(null);

  // Debounced fetch to prevent multiple rapid calls
  useEffect(() => {
    // Clear any pending debounce
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Cancel any ongoing fetch
    if (fetchAbortController.current) {
      fetchAbortController.current.abort();
    }

    // Debounce the fetch
    debounceTimer.current = setTimeout(() => {
      setHasFetched(false); // Reset fetch status when date changes
      fetchBookings();
      setCurrentPage(1); // Reset to first page when date changes
    }, 300); // 300ms debounce

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      if (fetchAbortController.current) {
        fetchAbortController.current.abort();
      }
    };
  }, [selectedDate]);

  const fetchBookings = useCallback(async () => {
    // Create new AbortController for this fetch
    fetchAbortController.current = new AbortController();
    const signal = fetchAbortController.current.signal;

    setLoading(true);
    setError(null);
    try {
      const dateStr = selectedDate.format('YYYY-MM-DD');
      const data = await getNoShowBookings(dateStr);
      if (signal.aborted) return;
      setBookings(data.bookings || []);
      setHasFetched(true);
    } catch (err) {
      if (!err.name || err.name !== 'AbortError') {
        setError('Failed to load no-show bookings. Please try again.');
      }
    } finally {
      if (!fetchAbortController.current?.signal.aborted) {
        setLoading(false);
      }
    }
  }, [selectedDate]);

  const handleDateChange = (newDate) => {
    if (newDate && moment.isMoment(newDate)) {
      setSelectedDate(newDate);
    }
  };

  const handlePrevClick = () => {
    const newDate = selectedDate.clone().subtract(1, 'days');
    setSelectedDate(newDate);
  };

  const handleNextClick = () => {
    const newDate = selectedDate.clone().add(1, 'days');
    setSelectedDate(newDate);
  };

  const handleToday = () => {
    setSelectedDate(moment());
  };

  const handleBookingClick = (bookingId) => {
    const safeBookingId = normalizeBookingId(bookingId);
    if (!safeBookingId) {
      setError('Invalid booking reference. Please refresh and try again.');
      return;
    }
    setSelectedBookingId(safeBookingId);
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setSelectedBookingId(null);
  };

  // Pagination logic
  const paginatedBookings = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return bookings.slice(startIndex, startIndex + itemsPerPage);
  }, [bookings, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(bookings.length / itemsPerPage);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Box sx={getDashboardPageShellSx(currentTheme)}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <DashboardHeader
            title="No Show"
            forceDark={isMobileLayout}
            breadcrumbs={[
              { label: 'Home', path: '/dashboard/' },
              { label: 'Booking', path: '/booking/' },
              { label: 'No Show' }
            ]}
            navLinks={[
              { url: '/booking/list/', label: 'Booking List' },
              { url: '/booking/checkin/', label: 'Check-In' },
              { url: '/booking/checkout/', label: 'Check-Out' },
            ]}
          />
        </Box>

        {/* Date Navigation and Total No Show - Same style as check-in */}
        <Paper
          elevation={0}
          sx={getDashboardPaperSx(currentTheme, 'controls')}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              flexWrap: { xs: 'wrap', sm: 'nowrap' },
            }}
          >
            {/* Datepicker on the left */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
              className="dashboard-date-range"
            >
              <IconButton
                onClick={handlePrevClick}
                className="calendar-nav-button"
                sx={{
                  borderRadius: '4px',
                  width: '36px',
                  height: '36px',
                  padding: 0,
                  color: isDark ? '#ddd' : '#333',
                  '&:hover': {
                    backgroundColor: isDark ? '#38373f' : '#e0e0e0',
                  },
                }}
              >
                <ChevronLeft />
              </IconButton>
              <Box sx={{ minWidth: '200px' }} className="calendar-date-picker">
                <DatePicker
                  value={selectedDate}
                  onChange={handleDateChange}
                  format={getHotelDateFormat()}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          height: '36px',
                          fontSize: '14px',
                          '& fieldset': {
                            borderColor: CUSTOM_BORDER_COLOR,
                          },
                          '&:hover fieldset': {
                            borderColor: CUSTOM_BORDER_COLOR,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: CUSTOM_BORDER_COLOR,
                          },
                        },
                        '& .MuiInputBase-input': {
                          padding: '8px 12px',
                          textAlign: 'center',
                          color: isDark ? '#ddd' : '#333',
                        },
                      },
                    },
                  }}
                />
              </Box>
              <IconButton
                onClick={handleNextClick}
                className="calendar-nav-button"
                sx={{
                  borderRadius: '4px',
                  width: '36px',
                  height: '36px',
                  padding: 0,
                  color: isDark ? '#ddd' : '#333',
                  '&:hover': {
                    backgroundColor: isDark ? '#38373f' : '#e0e0e0',
                  },
                }}
              >
                <ChevronRight />
              </IconButton>
            </Box>

            {/* Total No Show on the right */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  color: isDark ? '#999' : '#666',
                  fontWeight: 500,
                }}
              >
                Total No Show:
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: isDark ? '#ddd' : '#333',
                  fontWeight: 600,
                }}
              >
                {bookings.length}
              </Typography>
            </Box>
          </Box>
        </Paper>

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      {actionNotice && (
        <Alert
          severity={actionNotice.severity}
          sx={{ mb: 3 }}
          onClose={() => setActionNotice(null)}
        >
          {actionNotice.text}
        </Alert>
      )}

      {/* Bookings Grid */}
      {!loading && !error && hasFetched && (
        <>
          {bookings.length === 0 ? (
            <EmptyStateCard themeMode={currentTheme}>
              No pending no-show bookings for {selectedDate.format('DD MMM YYYY')}
            </EmptyStateCard>
          ) : (
            <>
              <Grid container spacing={3}>
                {paginatedBookings.map((booking) => {
                  const resolvedBookingId = normalizeBookingId(booking?.id || booking?.booking_id);
                  return (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={4}
                      lg={3}
                      sx={getBookingOpsGridItemSx()}
                      key={resolvedBookingId || `${booking?.code || 'booking'}_${booking?.check_in_date || ''}_${booking?.check_out_date || ''}`}
                    >
                      <BookingOpsBookingCard
                        booking={booking}
                        isDark={isDark}
                        accent={CUSTOM_BORDER_COLOR}
                        isMobile={isMobileLayout}
                        onClick={() => handleBookingClick(resolvedBookingId)}
                      />
                    </Grid>
                  );
                })}
              </Grid>
              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 2 }}>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    showFirstButton
                    showLastButton
                    sx={{
                      '& .MuiPaginationItem-root': {
                        color: isDark ? '#ddd' : '#333',
                        borderColor: isDark ? '#333246' : '#e0e0e0',
                        '&.Mui-selected': {
                          backgroundColor: CUSTOM_BORDER_COLOR,
                          color: '#000',
                          '&:hover': {
                            backgroundColor: CUSTOM_BORDER_COLOR,
                          },
                        },
                      },
                    }}
                  />
                </Box>
              )}
            </>
          )}
        </>
      )}

      {/* No Show Drawer */}
      {selectedBookingId && (
        <NoShowDrawer
          bookingId={selectedBookingId}
          open={drawerOpen}
          onClose={handleDrawerClose}
          onNoShowSuccess={(result) => {
            fetchBookings();
            const otaLabel = result?.ota?.partner_label || result?.channelhub?.partner_label || 'OTA';
            if (result?.marked && result?.ota?.success) {
              setActionNotice({
                severity: 'success',
                text: `No-show marked. ${otaLabel} accepted the update.`,
              });
            } else if (result?.needs_retry || result?.no_show_status === 'pending') {
              setActionNotice({
                severity: 'warning',
                text: result?.message || `Rooms released. No-show is still pending because ${otaLabel} did not accept it. Open the booking and mark no-show again.`,
              });
            } else if (result?.marked) {
              setActionNotice({
                severity: 'success',
                text: result?.message || 'Booking marked as no-show.',
              });
            }
          }}
        />
      )}
    </Box>
    </LocalizationProvider>
  );
};

export default NoShowPage;

